import { useEffect, useId, useState } from 'react'
import AdditionalSpecsEditor from './AdditionalSpecsEditor.jsx'
import DeletePartModal from './DeletePartModal.jsx'
import ProjectPicker from './ProjectPicker.jsx'
import StatusBadge from './StatusBadge.jsx'
import UnitSelect from './UnitSelect.jsx'
import { MANUFACTURER_SUGGESTIONS, MODEL_MATCH_OPTIONS, PART_SIZE_OPTIONS, STORAGE_METHOD_OPTIONS } from '../data/constants'
import { normalizePartProjects } from '../services/projectApi'
import { toSpecEditorRows } from '../utils/specs'
import {
  formatCurrentStock,
  getReviewStatus,
  isMergedPart,
  needsQuantityVerification,
} from '../utils/stock'

function buildForm(part) {
  return {
    category: part.category || '',
    manufacturer: part.manufacturer || '',
    model: part.model || '',
    name: part.name || '',
    description: part.description || part.specs || '',
    unit: part.unit || '개',
    ratedVoltage: part.ratedVoltage || '',
    ratedCurrent: part.ratedCurrent || '',
    poles: part.poles || '',
    breakingCapacity: part.breakingCapacity || '',
    power: part.power || '',
    contactConfiguration: part.contactConfiguration || '',
    coilVoltage: part.coilVoltage || '',
    communication: part.communication || '',
    dimensions: part.dimensions || '',
    powerType: part.powerType || '',
    additionalSpecs: toSpecEditorRows(part.additionalSpecs),
    inventoryNote: part.inventoryNote || '',
    note: part.note || part.memo || '',
    confirmedQuantity: '',
    modelMatchStatus: part.modelMatchStatus || '',
    size: part.size || '',
    storageMethod: part.storageMethod || '',
    location: part.location || '',
    projects: normalizePartProjects(part.projects),
  }
}

function toReviewPayload(form, { confirmQuantity = false, confirmModel = false } = {}) {
  const payload = {
    category: form.category,
    manufacturer: form.manufacturer,
    name: form.name,
    description: form.description,
    unit: form.unit,
    ratedVoltage: form.ratedVoltage,
    ratedCurrent: form.ratedCurrent,
    poles: form.poles,
    breakingCapacity: form.breakingCapacity,
    power: form.power,
    contactConfiguration: form.contactConfiguration,
    coilVoltage: form.coilVoltage,
    communication: form.communication,
    dimensions: form.dimensions,
    powerType: form.powerType,
    additionalSpecs: form.additionalSpecs,
    inventoryNote: form.inventoryNote,
    memo: form.note,
    size: form.size || '',
    storageMethod: form.storageMethod || '',
    location: form.location || '',
    modelMatchStatus: form.modelMatchStatus || '',
    projectIds: normalizePartProjects(form.projects).map((item) => item.projectId),
    confirmQuantity,
    confirmModel,
  }
  if (form.modelMatchStatus === 'MISMATCH') {
    payload.model = form.model
  }
  if (confirmQuantity) payload.confirmedQuantity = Number(form.confirmedQuantity)
  return payload
}

function ChoiceRow({ label, options, value, onChange }) {
  return (
    <fieldset className="review-step">
      <legend>{label}</legend>
      <div className="segmented" role="radiogroup" aria-label={label}>
        {options.map((option) => {
          const optionValue = typeof option === 'string' ? option : option.value
          const optionLabel = typeof option === 'string' ? option : option.label
          const selected = value === optionValue
          return (
            <button
              key={optionValue}
              type="button"
              className={`segmented__btn ${selected ? 'is-active' : ''}`}
              aria-pressed={selected}
              onClick={() => onChange(optionValue)}
            >
              {optionLabel}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

export default function ReviewPanel({ part, open, onClose, onSubmit, onSaveNext, canDelete = false, onDelete }) {
  const titleId = useId()
  const [form, setForm] = useState(() => (part ? buildForm(part) : null))
  const [error, setError] = useState('')
  const [saving, setSaving] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)

  useEffect(() => {
    if (!open || !part) return
    setForm(buildForm(part))
    setError('')
    setSaving('')
    setDeleteOpen(false)
  }, [open, part])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape' && !deleteOpen) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, deleteOpen])

  if (!open || !part || !form) return null

  const qtyNeeded = needsQuantityVerification(part)
  const review = getReviewStatus(part)
  const showDelete = canDelete && typeof onDelete === 'function' && !isMergedPart(part)

  function setField(name, value) {
    setForm((current) => ({ ...current, [name]: value }))
  }

  function parseConfirmedQuantity() {
    const raw = String(form.confirmedQuantity ?? '').trim()
    if (raw === '') return { error: '실제 수량을 입력해 주세요.' }
    const next = Number(raw)
    if (!Number.isInteger(next) || next < 0) {
      return { error: '실제 수량은 0 이상의 정수여야 합니다.' }
    }
    return { value: next }
  }

  function validateComplete() {
    if (qtyNeeded) {
      const parsed = parseConfirmedQuantity()
      if (parsed.error) return parsed.error
    }
    if (form.modelMatchStatus !== 'MATCH' && form.modelMatchStatus !== 'MISMATCH') {
      return '모델 일치 여부를 선택해 주세요.'
    }
    if (form.modelMatchStatus === 'MISMATCH' && !String(form.model || '').trim()) {
      return '모델이 불일치하면 실제 모델명을 입력해 주세요.'
    }
    return ''
  }

  async function run(action) {
    if (action === 'complete') {
      const message = validateComplete()
      if (message) {
        setError(message)
        return
      }
    }
    setSaving(action)
    setError('')
    try {
      const result = await onSubmit(
        toReviewPayload(form, {
          confirmQuantity: action === 'complete' && qtyNeeded,
          confirmModel: action === 'complete',
        }),
      )
      if (action === 'complete') await onSaveNext(result?.part)
    } catch (err) {
      setError(err.message || '권한이 없습니다.')
    } finally {
      setSaving('')
    }
  }

  async function handleDelete(target) {
    setSaving('delete')
    try {
      await onDelete(target)
      setDeleteOpen(false)
    } finally {
      setSaving('')
    }
  }

  return (
    <div className="review-drawer-backdrop" onClick={deleteOpen ? undefined : onClose} role="presentation">
      <div
        className="review-drawer"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="review-drawer__head">
          <div className="review-drawer__identity">
            <p className="review-drawer__code">{part.partCode || '관리번호 없음'}</p>
            <h2 id={titleId} className="review-drawer__title">
              {part.name || part.model || '-'}
            </h2>
            <dl className="review-identity">
              <div>
                <dt>관리번호</dt>
                <dd>{part.partCode || '-'}</dd>
              </div>
              <div>
                <dt>제조사</dt>
                <dd>{part.manufacturer || '-'}</dd>
              </div>
              <div>
                <dt>등록 모델</dt>
                <dd className="model-name">{part.model || '-'}</dd>
              </div>
              <div>
                <dt>품명</dt>
                <dd>{part.name || '-'}</dd>
              </div>
            </dl>
          </div>
          <div className="review-drawer__badges">
            <StatusBadge kind="verification" status={review} part={part} />
            {qtyNeeded ? <StatusBadge part={part} /> : null}
          </div>
        </div>

        <div className="review-drawer__body">
          {qtyNeeded ? (
            <div className="panel review-step">
              <label className="field" htmlFor="review-confirmed-qty">
                <span>① 실제 수량</span>
                <div className="review-qty-confirm__row">
                  <input
                    id="review-confirmed-qty"
                    className="input input--qty"
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={form.confirmedQuantity}
                    onChange={(event) => {
                      const value = event.target.value
                      if (value === '') {
                        setField('confirmedQuantity', '')
                        return
                      }
                      if (!/^\d+$/.test(value)) return
                      setField('confirmedQuantity', value)
                    }}
                    placeholder="0"
                  />
                  <span className="review-qty-unit">{form.unit || part.unit || '개'}</span>
                </div>
              </label>
              <p className="muted">현재수량 {formatCurrentStock(part)}</p>
            </div>
          ) : (
            <div className="panel review-step review-qty-box">
              <span>① 실제 수량</span>
              <strong>{formatCurrentStock(part)}</strong>
              <em className="muted">수량 확인 완료</em>
            </div>
          )}

          <label className="field review-step" htmlFor="review-manufacturer">
            <span>② 제조사</span>
            <input
              id="review-manufacturer"
              className="input"
              list="review-manufacturer-suggestions"
              value={form.manufacturer}
              onChange={(e) => setField('manufacturer', e.target.value)}
              placeholder="예: B&R, Phoenix Contact, LS ELECTRIC"
            />
            <datalist id="review-manufacturer-suggestions">
              {MANUFACTURER_SUGGESTIONS.map((item) => (
                <option key={item} value={item} />
              ))}
            </datalist>
          </label>

          <div className="review-step">
            <ChoiceRow
              label="③ 모델 일치 여부"
              options={MODEL_MATCH_OPTIONS}
              value={form.modelMatchStatus}
              onChange={(value) => {
                setField('modelMatchStatus', value)
                if (value === 'MATCH') setField('model', part.model || '')
              }}
            />
            {form.modelMatchStatus === 'MISMATCH' ? (
              <label className="field">
                <span>실제 모델명</span>
                <input
                  className="input input--model"
                  value={form.model}
                  onChange={(e) => setField('model', e.target.value)}
                  placeholder="현장에서 확인한 모델명"
                />
              </label>
            ) : (
              <p className="muted">등록 모델 {part.model || '-'}</p>
            )}
          </div>

          <ChoiceRow
            label="④ 크기"
            options={PART_SIZE_OPTIONS}
            value={form.size}
            onChange={(value) => setField('size', value)}
          />

          <ChoiceRow
            label="⑤ 보관방식"
            options={STORAGE_METHOD_OPTIONS}
            value={form.storageMethod}
            onChange={(value) => setField('storageMethod', value)}
          />

          <label className="field review-step">
            <span>⑥ 위치</span>
            <input
              className="input"
              value={form.location}
              onChange={(e) => setField('location', e.target.value)}
              placeholder="예: A-R02-S03"
            />
          </label>

          <div className="field review-step">
            <span>사용 프로젝트</span>
            <ProjectPicker
              multiple
              activeOnly
              value={form.projects}
              onChange={(projects) => setField('projects', projects)}
              placeholder="프로젝트 검색 후 선택"
            />
          </div>

          <label className="field review-step">
            <span>⑦ 메모</span>
            <textarea
              className="input input--area"
              rows={3}
              value={form.note}
              onChange={(e) => setField('note', e.target.value)}
              placeholder="선택 입력"
            />
          </label>

          {form.inventoryNote ? (
            <p className="muted review-note">조사 메모: {form.inventoryNote}</p>
          ) : null}

          <details className="review-specs">
            <summary>상세 사양 수정</summary>
            <div className="form-grid">
              <label className="field">
                <span>분류</span>
                <input className="input" value={form.category} onChange={(e) => setField('category', e.target.value)} />
              </label>
              <label className="field">
                <span>단위</span>
                <UnitSelect value={form.unit} onChange={(unit) => setField('unit', unit)} />
              </label>
              <label className="field field--wide">
                <span>품명</span>
                <input className="input" value={form.name} onChange={(e) => setField('name', e.target.value)} />
              </label>
              <label className="field">
                <span>정격전압</span>
                <input className="input" value={form.ratedVoltage} onChange={(e) => setField('ratedVoltage', e.target.value)} />
              </label>
              <label className="field">
                <span>정격전류</span>
                <input className="input" value={form.ratedCurrent} onChange={(e) => setField('ratedCurrent', e.target.value)} />
              </label>
              <label className="field">
                <span>극수</span>
                <input className="input" value={form.poles} onChange={(e) => setField('poles', e.target.value)} />
              </label>
              <label className="field">
                <span>차단용량</span>
                <input
                  className="input"
                  value={form.breakingCapacity}
                  onChange={(e) => setField('breakingCapacity', e.target.value)}
                />
              </label>
              <label className="field">
                <span>전력</span>
                <input className="input" value={form.power} onChange={(e) => setField('power', e.target.value)} />
              </label>
              <label className="field">
                <span>접점구성</span>
                <input
                  className="input"
                  value={form.contactConfiguration}
                  onChange={(e) => setField('contactConfiguration', e.target.value)}
                />
              </label>
              <label className="field">
                <span>코일전압</span>
                <input className="input" value={form.coilVoltage} onChange={(e) => setField('coilVoltage', e.target.value)} />
              </label>
              <label className="field">
                <span>통신방식</span>
                <input
                  className="input"
                  value={form.communication}
                  onChange={(e) => setField('communication', e.target.value)}
                />
              </label>
              <label className="field">
                <span>치수</span>
                <input className="input" value={form.dimensions} onChange={(e) => setField('dimensions', e.target.value)} />
              </label>
              <label className="field">
                <span>AC / DC</span>
                <input className="input" value={form.powerType} onChange={(e) => setField('powerType', e.target.value)} />
              </label>
              <label className="field field--wide">
                <span>설명</span>
                <textarea
                  className="input input--area"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                />
              </label>
            </div>
            <AdditionalSpecsEditor
              rows={form.additionalSpecs}
              onChange={(rows) => setField('additionalSpecs', rows)}
            />
          </details>

          {error ? <p className="form-error">{error}</p> : null}
        </div>

        <div className="review-drawer__actions">
          <div className="review-drawer__actions-row">
            <button type="button" className="btn btn--ghost btn--touch" onClick={onClose} disabled={Boolean(saving)}>
              닫기
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--touch"
              onClick={() => run('save')}
              disabled={Boolean(saving)}
            >
              {saving === 'save' ? '저장 중...' : '저장'}
            </button>
          </div>
          <button
            type="button"
            className="btn btn--primary btn--touch review-next-btn"
            onClick={() => run('complete')}
            disabled={Boolean(saving)}
          >
            {saving === 'complete' ? '처리 중...' : '검수 완료 후 다음 품목'}
          </button>
          {showDelete ? (
            <button
              type="button"
              className="btn btn--danger btn--touch review-delete-btn"
              onClick={() => setDeleteOpen(true)}
              disabled={Boolean(saving)}
            >
              잘못 등록된 품목 삭제
            </button>
          ) : null}
        </div>
      </div>
      <DeletePartModal
        part={part}
        open={deleteOpen && showDelete}
        saving={saving === 'delete'}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  )
}
